import React, { memo, useCallback, useEffect, useMemo, useRef, useState } from "react";
import { BASE_URL } from "../../const";
import Navbar from "../../components/Navbar";
import { ArrowDown, ArrowUp, ArrowUpDown, Loader2, X } from "lucide-react";
import { cn } from "../../lib/utils";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { Badge } from "../../components/ui/badge";
import { Checkbox } from "../../components/ui/checkbox";

// ─── Constants ────────────────────────────────────────────────────────────────

const PAGE_SIZE_OPTIONS = [25, 50, 100, 250, 500];

const COLUMNS = [
  { key: "id",                       label: "ID",          editable: false, width: 80  },
  { key: "sku_code",                  label: "SKU",         editable: false, width: 140 },
  { key: "sku_name",                  label: "SKU Name",    editable: false, width: 220 },
  { key: "mp_name",                   label: "MP Name",     editable: true,  width: 140 },
  { key: "listing_id",                label: "Listing ID",  editable: true,  width: 160 },
  { key: "ean_number",                label: "EAN",         editable: true,  width: 130 },
  { key: "product_id",                label: "Product ID",  editable: true,  width: 150 },
  { key: "title",                     label: "Title",       editable: true,  width: 240 },
  { key: "description",               label: "Description", editable: true,  width: 240 },
  { key: "other_listing_attributes",  label: "Other Attr",  editable: true,  width: 220 },
  { key: "status",                    label: "Status",      editable: true,  badge: true, width: 120 },
  { key: "status_update_date",        label: "Status Date", editable: true,  width: 130 },
  { key: "target_date",               label: "Target Date", editable: true,  width: 130 },
  { key: "created_at",                label: "Created At",  editable: false, width: 180 },
  { key: "updated_at",                label: "Updated At",  editable: false, width: 180 },
];

const DEFAULT_VISIBLE = ["id", "sku_code", "sku_name", "mp_name", "listing_id", "status", "status_update_date", "target_date"];
const DEFAULT_FILTERS = { search: "", sku: "", mp_name: "", status: "", listing_id: "" };

const STATUS_FILTER_FALLBACK = [
  { v: "active",     l: "Active"     },
  { v: "inactive",   l: "Inactive"   },
  { v: "suppressed", l: "Suppressed" },
  { v: "pending",    l: "Pending"    },
];

const IMPORTABLE_FIELDS = new Set(
  COLUMNS.map(c => c.key).filter(k => !["sku_name", "created_at", "updated_at"].includes(k))
);

const LISTING_IMPORT_COLUMNS = ["listing_id", "mp_name", "sku_code", "ean", "produt_id", "title", "descroption", "status"];
const LISTING_IMPORT_SAMPLE_ROWS = [{
  listing_id: "LST-AMZ-1001", mp_name: "amazon", sku_code: "SKU-ABC-001",
  ean: "8901234567890", produt_id: "B0ABC123",
  title: "Sample Product Title", descroption: "Sample listing description", status: "active",
}];
const LISTING_IMPORT_HEADER_ALIASES = {
  listing_id: "listing_id", mp_name: "mp_name", "mp name": "mp_name",
  sku: "sku_code", "sku code": "sku_code", sku_code: "sku_code",
  ean: "ean", ean_number: "ean", produt_id: "produt_id", product_id: "produt_id",
  title: "title", descroption: "descroption", description: "descroption", status: "status",
};

const SKU_POPUP_FIELDS = [
  "id", "business_type", "parent_sku", "sku", "sku_name", "sku_reference_code",
  "sku_type", "ean_upc", "hsn_code", "tax_rate", "mrp", "components",
  "category", "sub_category", "color", "pot", "sku_size", "sku_actual_dimensions",
  "sku_actual_weight_in_kg", "combo_of", "sku_cost", "sku_cost_tax",
  "sku_inner_packaging_w_tax", "sku_outer_packaging_w_tax",
  "mile_1_cost_w_tax", "mile_2_cost_w_tax", "processing_cost_w_tax",
  "sku_status", "sku_tag", "cost_audit", "attr_audit", "created_at", "updated_at",
];

// ─── API helpers ──────────────────────────────────────────────────────────────

function getAuthHeader() {
  try {
    const token = localStorage.getItem("token");
    if (!token) return {};
    if (token.startsWith("Bearer ") || token.startsWith("Token ")) return { Authorization: token };
    return { Authorization: `Token ${token}` };
  } catch { return {}; }
}

function parseListResponse(data) {
  if (Array.isArray(data?.results)) return data.results;
  if (Array.isArray(data?.data))    return data.data;
  if (Array.isArray(data))          return data;
  return [];
}

async function fetchRows({ page, pageSize, filters }) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (filters.search)     params.set("search",     filters.search);
  if (filters.sku)        params.set("sku",        filters.sku);
  if (filters.mp_name)    params.set("mp_name",    filters.mp_name);
  if (filters.status)     params.set("status",     filters.status);
  if (filters.listing_id) params.set("listing_id", filters.listing_id);
  const response = await fetch(`${BASE_URL}/listings/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch listings");
  return data;
}

async function fetchDropdownOptions(fieldName) {
  const params = new URLSearchParams({ field_name: fieldName });
  const response = await fetch(`${BASE_URL}/dropdowns/options/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || `Failed to fetch ${fieldName} options`);
  return parseListResponse(data)
    .map(row => ({ v: String(row?.value ?? "").trim(), l: String(row?.label ?? row?.value ?? "").trim() }))
    .filter(row => row.v);
}

async function fetchUnitEcoForSkuAndMp({ sku, mpName }) {
  const params = new URLSearchParams({ page_size: "1" });
  if (sku)    params.set("sku",     sku);
  if (mpName) params.set("mp_name", mpName);
  const response = await fetch(`${BASE_URL}/unit-economics/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch unit economics context");
  return parseListResponse(data)[0] || null;
}

async function fetchSkuMasterDetail({ sku, businessType }) {
  if (!sku) throw new Error("SKU is required");
  const params = new URLSearchParams();
  if (businessType) params.set("business_type", businessType);
  const response = await fetch(`${BASE_URL}/sku/${encodeURIComponent(sku)}/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch SKU details");
  return data?.data && typeof data.data === "object" ? data.data : data;
}

async function fetchSkuMasterFallback(sku) {
  const params = new URLSearchParams({ search: sku, page_size: "50" });
  const response = await fetch(`${BASE_URL}/sku/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Failed to fetch SKU fallback");
  const rows = parseListResponse(data);
  return rows.find(r => String(r?.sku || "").trim().toLowerCase() === String(sku || "").trim().toLowerCase()) || rows[0] || null;
}

async function bulkUpdate(payload) {
  const response = await fetch(`${BASE_URL}/listings/bulk-update/`, {
    method: "PUT",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Bulk update failed");
  return data?.data || data;
}

async function bulkCreate(payload) {
  const response = await fetch(`${BASE_URL}/listings/bulk-create/`, {
    method: "POST",
    headers: { "Content-Type": "application/json", ...getAuthHeader() },
    body: JSON.stringify(payload),
  });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Bulk create failed");
  return data?.data || data;
}

async function fetchExport({ page, pageSize, filters }) {
  const params = new URLSearchParams({ page: String(page), page_size: String(pageSize) });
  if (filters.search)     params.set("search",     filters.search);
  if (filters.sku)        params.set("sku",        filters.sku);
  if (filters.mp_name)    params.set("mp_name",    filters.mp_name);
  if (filters.status)     params.set("status",     filters.status);
  if (filters.listing_id) params.set("listing_id", filters.listing_id);
  const response = await fetch(`${BASE_URL}/listings/export/?${params.toString()}`, { headers: getAuthHeader() });
  const data = await response.json();
  if (!response.ok) throw new Error(data?.message || "Export failed");
  return data?.data?.rows || data?.rows || [];
}

// ─── CSV utilities ────────────────────────────────────────────────────────────

function csvSafe(v) {
  const s = String(v ?? "");
  return (s.includes(",") || s.includes('"') || s.includes("\n")) ? `"${s.replace(/"/g, '""')}"` : s;
}

function toCsv(rows, fields = Array.from(IMPORTABLE_FIELDS)) {
  return `${fields.join(",")}\n${rows.map(r => fields.map(f => csvSafe(r[f])).join(",")).join("\n")}`;
}

function downloadCsv(content, filename) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename;
  document.body.appendChild(a); a.click();
  document.body.removeChild(a); URL.revokeObjectURL(url);
}

function parseCSV(text) {
  const rows = []; let cur = "", line = [], inQ = false;
  for (let i = 0; i < text.length; i++) {
    const ch = text[i], nx = text[i + 1];
    if (ch === '"' && inQ && nx === '"') { cur += '"'; i++; continue; }
    if (ch === '"') { inQ = !inQ; continue; }
    if (ch === "," && !inQ) { line.push(cur); cur = ""; continue; }
    if ((ch === "\n" || ch === "\r") && !inQ) {
      if (ch === "\r" && nx === "\n") i++;
      line.push(cur); rows.push(line); line = []; cur = ""; continue;
    }
    cur += ch;
  }
  if (cur.length > 0 || line.length > 0) { line.push(cur); rows.push(line); }
  return rows;
}

function fmtDate(v) {
  if (!v) return "—";
  const d = new Date(v);
  return isNaN(d.getTime()) ? String(v) : d.toLocaleString();
}

// ─── Shared style ─────────────────────────────────────────────────────────────

const SELECT_CLS = "flex h-9 w-full rounded-md border border-input bg-background px-3 py-1 text-sm shadow-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer";

// ─── Toast ────────────────────────────────────────────────────────────────────

const Toast = memo(({ message, type, onClose }) => {
  useEffect(() => { const t = setTimeout(onClose, 4000); return () => clearTimeout(t); }, [onClose]);
  const wrapperCls = {
    success: "border-green-500 bg-green-50 text-green-800",
    error:   "border-red-500 bg-red-50 text-red-800",
    warning: "border-yellow-500 bg-yellow-50 text-yellow-800",
    info:    "border-blue-500 bg-blue-50 text-blue-800",
  };
  const dotCls = { success: "bg-green-500", error: "bg-red-500", warning: "bg-yellow-500", info: "bg-blue-500" };
  return (
    <div className={cn(
      "fixed bottom-6 right-6 z-[9999] flex items-center gap-3 max-w-sm",
      "rounded-lg border-2 px-4 py-3 shadow-xl text-sm",
      wrapperCls[type] || wrapperCls.info
    )}>
      <span className={cn("w-2 h-2 rounded-full shrink-0", dotCls[type] || dotCls.info)} />
      <span className="flex-1">{message}</span>
      <button onClick={onClose} className="shrink-0 opacity-60 hover:opacity-100 transition-opacity">
        <X className="h-4 w-4" />
      </button>
    </div>
  );
});

// ─── EditableCell ─────────────────────────────────────────────────────────────

const EditableCell = memo(({ value, rowId, colKey, onChange }) => {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft]     = useState(String(value ?? ""));

  const commit = useCallback(() => {
    setEditing(false);
    if (draft !== String(value ?? "")) onChange(rowId, colKey, draft);
  }, [draft, value, rowId, colKey, onChange]);

  if (editing) return (
    <Input
      autoFocus
      value={draft}
      onChange={e => setDraft(e.target.value)}
      onBlur={commit}
      onKeyDown={e => {
        if (e.key === "Enter")  commit();
        if (e.key === "Escape") { setDraft(String(value ?? "")); setEditing(false); }
      }}
      className="h-7 text-xs py-0 px-1.5"
    />
  );

  return (
    <span
      onClick={() => { setDraft(String(value ?? "")); setEditing(true); }}
      title="Click to edit"
      className={cn(
        "block w-full cursor-text truncate px-0.5 py-0.5 rounded text-sm transition-colors hover:bg-accent",
        value ? "text-foreground" : "text-muted-foreground"
      )}
    >
      {String(value ?? "—")}
    </span>
  );
});

// ─── SortIcon ─────────────────────────────────────────────────────────────────

function SortIcon({ dir }) {
  if (dir === "asc")  return <ArrowUp   className="w-3 h-3 text-primary shrink-0" />;
  if (dir === "desc") return <ArrowDown className="w-3 h-3 text-primary shrink-0" />;
  return <ArrowUpDown className="w-3 h-3 text-muted-foreground/40 shrink-0" />;
}

// ─── StatusBadge ──────────────────────────────────────────────────────────────

const STATUS_STYLES = {
  active:     "bg-green-100 text-green-700 border-green-200",
  inactive:   "bg-red-100 text-red-700 border-red-200",
  suppressed: "bg-red-100 text-red-700 border-red-200",
  pending:    "bg-yellow-100 text-yellow-700 border-yellow-200",
};

function StatusBadge({ value }) {
  if (!value) return <span className="text-muted-foreground text-xs">—</span>;
  const label = String(value).replace(/_/g, " ");
  const style = STATUS_STYLES[String(value).toLowerCase()];
  if (!style) return <span className="text-muted-foreground text-xs capitalize">{label}</span>;
  return <Badge className={cn("capitalize text-xs font-medium", style)}>{label}</Badge>;
}

// ─── ResizeHandle ─────────────────────────────────────────────────────────────

const ResizeHandle = ({ onMouseDown }) => (
  <div
    onMouseDown={onMouseDown}
    className="absolute right-0 top-0 bottom-0 w-1 cursor-col-resize hover:bg-primary/50 transition-colors"
  />
);

// ─── ColumnPicker ─────────────────────────────────────────────────────────────

const ColumnPicker = memo(({ all, visible, onToggle, onSelectAll, onReset }) => (
  <div className="bg-white border border-border rounded-xl p-5 mb-3">
    <div className="flex items-center justify-between mb-3">
      <span className="text-sm text-muted-foreground">
        Visible: <strong className="text-foreground">{visible.length}</strong> / {all.length}
      </span>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={onSelectAll}>All</Button>
        <Button variant="outline" size="sm" onClick={onReset}>Reset</Button>
      </div>
    </div>
    <div className="grid gap-1.5" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(160px, 1fr))" }}>
      {all.map(col => {
        const on = visible.includes(col.key);
        return (
          <label
            key={col.key}
            className={cn(
              "flex items-center gap-2 cursor-pointer px-2.5 py-1.5 rounded-lg text-sm border transition-all",
              on ? "bg-violet-100 text-violet-700 border-violet-300" : "bg-muted/40 text-muted-foreground border-transparent hover:bg-muted"
            )}
          >
            <Checkbox
              checked={on}
              onCheckedChange={() => onToggle(col.key)}
              className={on ? "border-violet-400 data-[state=checked]:bg-violet-600 data-[state=checked]:border-violet-600" : ""}
            />
            {col.label}
          </label>
        );
      })}
    </div>
  </div>
));

// ─── FilterPanel ──────────────────────────────────────────────────────────────

const FilterPanel = memo(({ filters, setFilters, marketplaceOptions, statusOptions, onApply, onClear }) => (
  <div className="bg-white border border-border rounded-xl p-5 mb-3">
    <div className="grid gap-3" style={{ gridTemplateColumns: "repeat(auto-fill, minmax(170px, 1fr))" }}>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">SKU</label>
        <Input placeholder="Exact SKU" value={filters.sku} onChange={e => setFilters(p => ({ ...p, sku: e.target.value }))} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Listing ID</label>
        <Input placeholder="Listing ID" value={filters.listing_id} onChange={e => setFilters(p => ({ ...p, listing_id: e.target.value }))} />
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">MP Name</label>
        <select value={filters.mp_name} onChange={e => setFilters(p => ({ ...p, mp_name: e.target.value }))} className={SELECT_CLS}>
          <option value="">All</option>
          {marketplaceOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
      <div className="flex flex-col gap-1">
        <label className="text-xs font-semibold text-muted-foreground uppercase tracking-wide">Status</label>
        <select value={filters.status} onChange={e => setFilters(p => ({ ...p, status: e.target.value }))} className={SELECT_CLS}>
          <option value="">All</option>
          {statusOptions.map(o => <option key={o.v} value={o.v}>{o.l}</option>)}
        </select>
      </div>
    </div>
    <div className="flex gap-2 mt-4">
      <Button size="sm" onClick={onApply}>Apply filters</Button>
      <Button size="sm" variant="outline" onClick={onClear}>Clear all</Button>
    </div>
  </div>
));

// ─── SkuDetailModal ───────────────────────────────────────────────────────────

function SkuDetailModal({ popup, onClose }) {
  return (
    <div className="fixed inset-0 z-[5001] bg-black/40 flex items-center justify-center">
      <div className="bg-white rounded-2xl border border-border shadow-2xl w-[760px] max-w-[95vw] max-h-[85vh] flex flex-col overflow-hidden">
        <div className="px-5 py-4 border-b border-border flex items-start justify-between shrink-0">
          <div>
            <h3 className="text-base font-semibold text-foreground">SKU Details</h3>
            <p className="text-xs text-muted-foreground mt-0.5">
              SKU: {popup.context?.sku || "—"} | MP: {popup.context?.mpName || "—"}
            </p>
          </div>
          <button onClick={onClose} className="text-muted-foreground hover:text-foreground transition-colors">
            <X className="h-5 w-5" />
          </button>
        </div>
        <div className="p-4 overflow-auto">
          {popup.loading ? (
            <div className="flex items-center gap-2 text-sm text-muted-foreground py-8 justify-center">
              <Loader2 className="h-4 w-4 animate-spin" /> Loading SKU details…
            </div>
          ) : popup.error ? (
            <p className="text-sm text-red-600 py-4">{popup.error}</p>
          ) : (
            <div className="grid grid-cols-2 gap-2">
              {SKU_POPUP_FIELDS.map(key => (
                <div key={key} className="border border-border rounded-lg px-3 py-2 bg-white">
                  <div className="text-xs text-muted-foreground uppercase mb-1">{key}</div>
                  <div className="text-sm font-semibold text-foreground break-words">
                    {(() => {
                      const v = popup.data?.[key];
                      if (v == null || v === "") return "—";
                      if (typeof v === "object") return JSON.stringify(v);
                      return String(v);
                    })()}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── ActionsModal ─────────────────────────────────────────────────────────────

function ActionsModal({ onClose, selectedRows, onUpdateSelected, onUploadImport, onUploadBulkUpdate, onDownloadSample, showMessage }) {
  const [busy, setBusy] = useState({ downloading: false, uploading: false, updating: false });
  const isBusy = Object.values(busy).some(Boolean);
  const set = k => v => setBusy(p => ({ ...p, [k]: v }));

  const dlSelected = async () => {
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("downloading")(true);
    try {
      downloadCsv(toCsv(selectedRows), `listing_selected_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Downloaded ${selectedRows.length} rows.`);
    } finally { set("downloading")(false); }
  };

  const handleFile = (handler) => async (e) => {
    const file = e.target.files?.[0];
    if (!file || isBusy) { e.target.value = ""; return; }
    set("uploading")(true);
    try { await handler(await file.text()); }
    catch (err) { showMessage(err.message || "Upload failed", "error"); }
    finally { set("uploading")(false); e.target.value = ""; }
  };

  const updateSel = async () => {
    if (!selectedRows.length) { showMessage("Select at least one row.", "warning"); return; }
    set("updating")(true);
    try { await onUpdateSelected(); }
    catch (e) { showMessage(e.message || "Update failed", "error"); }
    finally { set("updating")(false); }
  };

  return (
    <div className="fixed z-[5000]" style={{ top: 140, right: "max(20px, calc((100vw - 1600px) / 2 + 20px))" }}>
      <div className="bg-white rounded-2xl border border-border shadow-2xl p-6 w-[440px] max-w-[92vw]">
        <div className="flex items-center justify-between mb-1">
          <h3 className="text-lg font-semibold text-foreground">Actions</h3>
          {!isBusy && (
            <Button variant="ghost" size="icon" className="h-8 w-8" onClick={onClose}>
              <X className="h-4 w-4" />
            </Button>
          )}
        </div>
        <p className="text-sm text-muted-foreground mb-5">Download, update, or import listing rows.</p>

        <div className="flex flex-col gap-2.5">
          <Button className="w-full gap-1" onClick={dlSelected} disabled={isBusy}>
            {busy.downloading && <Loader2 className="h-3 w-3 animate-spin" />}
            {busy.downloading ? "Downloading…" : "Download Selected"}
          </Button>

          {selectedRows.length > 0 && (
            <Button onClick={updateSel} disabled={isBusy} className="w-full gap-1">
              {busy.updating && <Loader2 className="h-4 w-4 animate-spin" />}
              {busy.updating ? "Updating…" : `Update Selected (${selectedRows.length})`}
            </Button>
          )}
        </div>

        {isBusy && (
          <div className="mt-4 flex items-center gap-2 text-sm text-primary">
            <Loader2 className="h-4 w-4 animate-spin" />
            {busy.uploading ? "Uploading…" : busy.downloading ? "Downloading…" : "Updating…"}
          </div>
        )}

        <div className="mt-4 p-4 bg-muted/40 rounded-xl border border-dashed border-border">
          <div className="flex items-center justify-between mb-3">
            <span className="text-sm font-semibold text-foreground">CSV Tools</span>
            <Button size="sm" variant="outline" onClick={onDownloadSample}>Sample CSV</Button>
          </div>
          <div className="grid grid-cols-2 gap-2">
            <label className={cn(
              "flex items-center justify-center text-xs font-semibold cursor-pointer",
              "border border-blue-200 bg-blue-50 text-blue-700 rounded-lg py-2.5 px-3",
              isBusy && "opacity-50 pointer-events-none"
            )}>
              Bulk Import CSV
              <input type="file" accept=".csv,text/csv" disabled={isBusy} onChange={handleFile(onUploadImport)} className="hidden" />
            </label>
            <label className={cn(
              "flex items-center justify-center text-xs font-semibold cursor-pointer",
              "border border-border bg-white text-muted-foreground rounded-lg py-2.5 px-3",
              isBusy && "opacity-50 pointer-events-none"
            )}>
              Bulk Update CSV
              <input type="file" accept=".csv,text/csv" disabled={isBusy} onChange={handleFile(onUploadBulkUpdate)} className="hidden" />
            </label>
          </div>
          <p className="text-xs text-muted-foreground mt-2">
            Import: creates new rows. Update: rows with <code>id</code> are updated via bulk-update endpoint.
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── DataTableRow ─────────────────────────────────────────────────────────────

const DataTableRow = memo(function DataTableRow({ row, rowIndex, columns, isSelected, onToggleRow, onCellChange, onOpenSkuPopup, colWidths }) {
  return (
    <tr className={cn(
      "border-t border-border transition-colors",
      isSelected ? "bg-violet-50" : rowIndex % 2 === 0 ? "bg-white" : "bg-slate-50/50",
      !isSelected && "hover:bg-slate-50"
    )}>
      <td className="px-3 py-2 text-center w-11">
        <Checkbox checked={isSelected} onCheckedChange={() => onToggleRow(row.id)} />
      </td>
      {columns.map(col => {
        const raw = row[col.key];
        const value = raw && typeof raw === "object" ? JSON.stringify(raw) : raw;
        const isDate = col.key.endsWith("_date") || col.key.endsWith("_at");

        return (
          <td key={col.key} className="px-3 py-2 text-sm align-middle overflow-hidden">
            {col.badge ? (
              <StatusBadge value={raw} />
            ) : col.key === "sku_code" ? (
              <button
                type="button"
                onClick={() => onOpenSkuPopup(row)}
                title="Click to view SKU details"
                className="text-blue-600 font-bold underline text-sm hover:text-blue-800 whitespace-nowrap"
              >
                {value ?? "—"}
              </button>
            ) : col.editable ? (
              <EditableCell value={value} rowId={row.id} colKey={col.key} onChange={onCellChange} />
            ) : (
              <span className="text-muted-foreground text-xs whitespace-nowrap">
                {isDate ? fmtDate(value) : (value ?? "—")}
              </span>
            )}
          </td>
        );
      })}
    </tr>
  );
}, (prev, next) => (
  prev.row === next.row &&
  prev.rowIndex === next.rowIndex &&
  prev.columns === next.columns &&
  prev.isSelected === next.isSelected &&
  prev.onToggleRow === next.onToggleRow &&
  prev.onCellChange === next.onCellChange &&
  prev.onOpenSkuPopup === next.onOpenSkuPopup
));

// ─── DataTable ────────────────────────────────────────────────────────────────

const DataTable = memo(function DataTable({ columns, rows, loading, allChecked, selectedIds, onToggleAll, onToggleRow, onCellChange, sort, onSort, colWidths, onResizeStart, onOpenSkuPopup }) {
  const someSelected = rows.some(r => selectedIds[r.id]);

  return (
    <div className="border border-border rounded-xl overflow-hidden bg-white">
      <div className="overflow-x-auto overflow-y-auto" style={{ maxHeight: "calc(100vh - 340px)" }}>
        <table className="border-collapse w-full text-sm" style={{ tableLayout: "fixed" }}>
          <colgroup>
            <col style={{ width: 44 }} />
            {columns.map(col => <col key={col.key} style={{ width: colWidths[col.key] || col.width || 120 }} />)}
          </colgroup>
          <thead>
            <tr className="bg-slate-50 sticky top-0 z-10 border-b border-border">
              <th className="px-3 py-2.5 text-center w-11">
                <Checkbox
                  checked={allChecked ? true : someSelected ? "indeterminate" : false}
                  onCheckedChange={onToggleAll}
                />
              </th>
              {columns.map(col => {
                const sorted = sort?.key === col.key;
                return (
                  <th
                    key={col.key}
                    onClick={() => onSort(col.key)}
                    className="px-3 py-2.5 text-left text-xs font-semibold text-muted-foreground uppercase tracking-wide whitespace-nowrap cursor-pointer select-none relative"
                  >
                    <span className="flex items-center gap-1">
                      {col.label}
                      <SortIcon dir={sorted ? sort.dir : null} />
                    </span>
                    <ResizeHandle onMouseDown={e => onResizeStart(e, col.key)} />
                  </th>
                );
              })}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground">
                  <Loader2 className="h-5 w-5 animate-spin inline mr-2" />Loading…
                </td>
              </tr>
            ) : rows.length === 0 ? (
              <tr>
                <td colSpan={columns.length + 1} className="py-12 text-center text-muted-foreground text-sm">
                  No rows found. Try adjusting your filters.
                </td>
              </tr>
            ) : rows.map((row, ri) => (
              <DataTableRow
                key={row.id}
                row={row}
                rowIndex={ri}
                columns={columns}
                isSelected={!!selectedIds[row.id]}
                onToggleRow={onToggleRow}
                onCellChange={onCellChange}
                onOpenSkuPopup={onOpenSkuPopup}
                colWidths={colWidths}
              />
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
});

// ─── ListingMaster (main page) ────────────────────────────────────────────────

export default function ListingMaster() {
  const [rows,             setRows]             = useState([]);
  const [loading,          setLoading]          = useState(false);
  const [count,            setCount]            = useState(0);
  const [hasNextPage,      setHasNextPage]      = useState(false);
  const [hasPrevPage,      setHasPrevPage]      = useState(false);
  const [page,             setPage]             = useState(1);
  const [pageSize,         setPageSize]         = useState(50);
  const [filters,          setFilters]          = useState({ ...DEFAULT_FILTERS });
  const [appliedFilters,   setAppliedFilters]   = useState({ ...DEFAULT_FILTERS });
  const [selectedIds,      setSelectedIds]      = useState({});
  const [visibleKeys,      setVisibleKeys]      = useState(DEFAULT_VISIBLE);
  const [marketplaceOpts,  setMarketplaceOpts]  = useState([]);
  const [statusOpts,       setStatusOpts]       = useState(STATUS_FILTER_FALLBACK);
  const [sort,             setSort]             = useState(null);
  const [colWidths,        setColWidths]        = useState({});
  const [panel,            setPanel]            = useState(null);
  const [showActions,      setShowActions]      = useState(false);
  const [skuPopup,         setSkuPopup]         = useState({ open: false, loading: false, error: "", context: null, data: null });
  const [toast,            setToast]            = useState(null);

  const visibleCols  = useMemo(() => COLUMNS.filter(c => visibleKeys.includes(c.key)), [visibleKeys]);
  const selectedRows = useMemo(() => rows.filter(r => selectedIds[r.id]), [rows, selectedIds]);
  const allChecked   = useMemo(() => rows.length > 0 && rows.every(r => selectedIds[r.id]), [rows, selectedIds]);
  const totalPages   = Math.max(1, Math.ceil(count / pageSize));

  const showMessage = useCallback((msg, type = "success") => setToast({ message: msg, type }), []);

  // Column resize
  const resizeRef = useRef(null);
  const handleResizeStart = useCallback((e, key) => {
    e.preventDefault();
    const startX = e.clientX;
    const startW = colWidths[key] || COLUMNS.find(c => c.key === key)?.width || 120;
    resizeRef.current = { key, startX, startW };
    const onMove = mv => {
      const { key, startX, startW } = resizeRef.current;
      setColWidths(p => ({ ...p, [key]: Math.max(60, startW + mv.clientX - startX) }));
    };
    const onUp = () => {
      resizeRef.current = null;
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("mouseup", onUp);
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("mouseup", onUp);
  }, [colWidths]);

  const handleSort = useCallback(key => {
    setSort(prev => {
      if (prev?.key === key) return prev.dir === "asc" ? { key, dir: "desc" } : null;
      return { key, dir: "asc" };
    });
    setPage(1);
  }, []);

  // Sort client-side
  const sortedRows = useMemo(() => {
    if (!sort?.key) return rows;
    return [...rows].sort((a, b) => {
      const av = String(a[sort.key] ?? "").toLowerCase();
      const bv = String(b[sort.key] ?? "").toLowerCase();
      if (av < bv) return sort.dir === "asc" ? -1 : 1;
      if (av > bv) return sort.dir === "asc" ? 1 : -1;
      return 0;
    });
  }, [rows, sort]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      const data = await fetchRows({ page, pageSize, filters: appliedFilters });
      setRows(parseListResponse(data));
      setCount(Number(data?.count || 0));
      setHasNextPage(Boolean(data?.next));
      setHasPrevPage(Boolean(data?.previous));
      setSelectedIds({});
    } catch (e) { showMessage(e.message || "Failed to load data", "error"); }
    finally { setLoading(false); }
  }, [appliedFilters, page, pageSize, showMessage]);

  const loadDropdowns = useCallback(async () => {
    const [statusRes, mpRes] = await Promise.allSettled([
      fetchDropdownOptions("status"),
      fetchDropdownOptions("marketplace"),
    ]);
    if (statusRes.status === "fulfilled" && statusRes.value.length) setStatusOpts(statusRes.value);
    if (mpRes.status === "fulfilled" && mpRes.value.length) setMarketplaceOpts(mpRes.value);
  }, []);

  useEffect(() => { loadData(); },     [loadData]);
  useEffect(() => { loadDropdowns(); }, [loadDropdowns]);

  const handleCellChange = useCallback((rowId, key, val) => {
    setRows(prev => {
      const i = prev.findIndex(r => r.id === rowId);
      if (i === -1) return prev;
      const next = [...prev];
      next[i] = { ...prev[i], [key]: val };
      return next;
    });
  }, []);

  const openSkuPopup = useCallback(async row => {
    const sku    = String(row?.sku_code || "").trim();
    const mpName = String(row?.mp_name  || "").trim();
    if (!sku) { showMessage("SKU value not found.", "warning"); return; }

    setSkuPopup({ open: true, loading: true, error: "", context: { sku, mpName }, data: null });
    try {
      const ueRow = await fetchUnitEcoForSkuAndMp({ sku, mpName });
      const businessType = String(ueRow?.product_cost_tag || ueRow?.business_type || ueRow?.cost_tag || "").trim();
      let skuData = null;
      try {
        skuData = await fetchSkuMasterDetail({ sku, businessType });
      } catch {
        skuData = await fetchSkuMasterFallback(sku);
        if (!skuData) throw new Error("SKU not found");
      }
      setSkuPopup({ open: true, loading: false, error: "", context: { sku, mpName }, data: skuData });
    } catch (e) {
      setSkuPopup({ open: true, loading: false, error: e.message || "Failed to load SKU details", context: { sku, mpName }, data: null });
    }
  }, [showMessage]);

  const handleUpdateSelected = async () => {
    const editableKeys = COLUMNS.filter(c => c.editable !== false).map(c => c.key);
    const payload = selectedRows.map(row => {
      const obj = { id: row.id };
      editableKeys.forEach(k => {
        let value = row[k];
        if (typeof value === "string") { const t = value.trim(); value = t === "" ? null : t; }
        obj[k] = value ?? null;
      });
      return obj;
    });
    const res = await bulkUpdate(payload);
    showMessage(`Updated: ${Number(res.updated || 0)}, Failed: ${Number(res.failed || 0)}`, Number(res.failed || 0) ? "warning" : "success");
    await loadData();
  };

  const handleImportCsv = async text => {
    const matrix = parseCSV(text);
    if (!matrix.length) throw new Error("CSV file is empty");
    const headers = matrix[0].map(h => {
      const key = String(h || "").trim().toLowerCase().replace(/\*/g, "").trim();
      return LISTING_IMPORT_HEADER_ALIASES[key] || String(h || "").trim();
    });
    const parsed = matrix.slice(1)
      .filter(line => line.some(c => String(c || "").trim() !== ""))
      .map(line => {
        const obj = {};
        headers.forEach((h, i) => {
          if (!LISTING_IMPORT_COLUMNS.includes(h)) return;
          const raw = String(line[i] ?? "").trim();
          obj[h] = raw === "" ? null : raw;
        });
        return obj;
      });
    if (!parsed.length) throw new Error("No valid rows in CSV");
    const res = await bulkCreate(parsed);
    showMessage(`Imported: ${Number(res.created || 0)}, Skipped: ${Number(res.failed || 0)}`, Number(res.failed || 0) ? "warning" : "success");
    await loadData();
  };

  const handleBulkUpdateCsv = async (text) => {
    const matrix = parseCSV(text);
    if (!matrix.length) throw new Error("CSV file is empty");
    const headers = matrix[0].map(h => {
      const key = String(h || "").trim().toLowerCase().replace(/\*/g, "").trim();
      return LISTING_IMPORT_HEADER_ALIASES[key] || String(h || "").trim();
    });
    const parsed = matrix.slice(1)
      .filter(line => line.some(c => String(c || "").trim() !== ""))
      .map(line => {
        const obj = {};
        headers.forEach((h, i) => {
          if (!IMPORTABLE_FIELDS.has(h) && h !== "id") return;
          const raw = String(line[i] ?? "").trim();
          obj[h] = raw === "" ? null : raw;
        });
        return obj;
      });
    const updates = parsed
      .map(r => ({ id: r.id, ...Object.fromEntries(Object.entries(r).filter(([k]) => k !== "id" && r[k] != null)) }))
      .filter(r => r.id);
    if (!updates.length) throw new Error("Bulk update CSV must include rows with an id column");
    const skipped = parsed.length - updates.length;
    const res = await bulkUpdate(updates);
    showMessage(
      `Updated: ${Number(res.updated || 0)}, Failed: ${Number(res.failed || 0)}${skipped ? `, Skipped (no id): ${skipped}` : ""}`,
      Number(res.failed || 0) ? "warning" : "success"
    );
    await loadData();
  };

  const handleDownloadSample = () => {
    downloadCsv(toCsv(LISTING_IMPORT_SAMPLE_ROWS, LISTING_IMPORT_COLUMNS), "listing_import_sample.csv");
    showMessage("Sample CSV downloaded.");
  };

  const [exporting, setExporting] = useState(false);
  const handleExport = useCallback(async () => {
    setExporting(true);
    try {
      const rows = await fetchExport({ page, pageSize, filters: appliedFilters });
      if (!rows.length) { showMessage("No rows to export on this page.", "warning"); return; }
      const fields = Object.keys(rows[0]);
      downloadCsv(toCsv(rows, fields), `listing_export_p${page}_${new Date().toISOString().slice(0, 10)}.csv`);
      showMessage(`Exported ${rows.length} rows (page ${page}).`);
    } catch (e) { showMessage(e.message || "Export failed", "error"); }
    finally { setExporting(false); }
  }, [page, pageSize, appliedFilters, showMessage]);

  const toggleRow = useCallback(id => setSelectedIds(p => ({ ...p, [id]: !p[id] })), []);
  const toggleAll = useCallback(() => {
    if (allChecked) { setSelectedIds({}); return; }
    const n = {}; rows.forEach(r => { n[r.id] = true; }); setSelectedIds(n);
  }, [allChecked, rows]);

  const applyFilters = useCallback(() => {
    setAppliedFilters({ ...filters, search: filters.search.trim() });
    setPage(1);
  }, [filters]);
  const clearFilters = useCallback(() => {
    setFilters({ ...DEFAULT_FILTERS }); setAppliedFilters({ ...DEFAULT_FILTERS }); setPage(1);
  }, []);

  const togglePanel = k => setPanel(p => p === k ? null : k);
  const hasActiveFilters = Object.values(appliedFilters).some(Boolean);

  const pageNumbers = Array.from({ length: Math.min(5, totalPages) }, (_, i) => {
    const start = Math.max(1, Math.min(page - 2, totalPages - 4));
    return start + i;
  });

  return (
    <div className="min-h-screen bg-slate-50/70">
      <Navbar />

      <style>{`
        ::-webkit-scrollbar { width: 6px; height: 6px; }
        ::-webkit-scrollbar-track { background: #f1f5f9; }
        ::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 3px; }
        ::-webkit-scrollbar-thumb:hover { background: #94a3b8; }
      `}</style>

      {toast && <Toast message={toast.message} type={toast.type} onClose={() => setToast(null)} />}

      {skuPopup.open && (
        <SkuDetailModal
          popup={skuPopup}
          onClose={() => setSkuPopup({ open: false, loading: false, error: "", context: null, data: null })}
        />
      )}

      {showActions && (
        <ActionsModal
          onClose={() => setShowActions(false)}
          selectedRows={selectedRows}
          onUpdateSelected={handleUpdateSelected}
          onUploadImport={handleImportCsv}
          onUploadBulkUpdate={handleBulkUpdateCsv}
          onDownloadSample={handleDownloadSample}
          showMessage={showMessage}
        />
      )}

      <div className="max-w-[1600px] mx-auto px-5 py-6">

        {/* ── Page header ── */}
        <div className="flex items-end justify-between mb-5 flex-wrap gap-3">
          <div>
            <h1 className="text-2xl font-bold text-foreground tracking-tight">Listing Master</h1>
            <p className="text-sm text-muted-foreground mt-1">Manage listings per SKU × marketplace</p>
          </div>
          <div className="flex gap-2">
            <Button variant="outline" onClick={handleExport} disabled={exporting}>
              {exporting && <Loader2 className="h-4 w-4 animate-spin mr-1" />}
              {exporting ? "Exporting…" : "Export Page"}
            </Button>
            <Button onClick={() => setShowActions(true)}>Actions</Button>
          </div>
        </div>

        {/* ── Toolbar ── */}
        <div className="bg-white border border-border rounded-xl px-4 py-3 mb-3 flex items-center gap-2.5 flex-wrap">
          <Input
            className="flex-1 min-w-[220px]"
            placeholder="Search SKU, title, listing…"
            value={filters.search}
            onChange={e => setFilters(p => ({ ...p, search: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter") applyFilters(); }}
          />
          <Button variant={panel === "filters" ? "default" : "outline"} size="sm" onClick={() => togglePanel("filters")}>
            {`Filters${hasActiveFilters ? " ●" : ""}`}
          </Button>
          <Button variant={panel === "columns" ? "default" : "outline"} size="sm" onClick={() => togglePanel("columns")}>
            Columns
          </Button>
          <Button variant="outline" size="sm" onClick={applyFilters}>Search</Button>
          <select
            value={pageSize}
            onChange={e => { setPageSize(Number(e.target.value)); setPage(1); }}
            className="h-9 rounded-md border border-input bg-background px-3 text-sm focus:outline-none focus:ring-1 focus:ring-ring cursor-pointer"
          >
            {PAGE_SIZE_OPTIONS.map(s => <option key={s} value={s}>{s} / page</option>)}
          </select>
        </div>

        {/* ── Panels ── */}
        {panel === "filters" && (
          <FilterPanel
            filters={filters}
            setFilters={setFilters}
            marketplaceOptions={marketplaceOpts}
            statusOptions={statusOpts}
            onApply={applyFilters}
            onClear={clearFilters}
          />
        )}
        {panel === "columns" && (
          <ColumnPicker
            all={COLUMNS}
            visible={visibleKeys}
            onToggle={key => setVisibleKeys(p => {
              if (p.includes(key)) {
                if (p.length === 1) { showMessage("At least one column must be visible.", "warning"); return p; }
                return p.filter(k => k !== key);
              }
              return [...p, key];
            })}
            onSelectAll={() => setVisibleKeys(COLUMNS.map(c => c.key))}
            onReset={() => { setVisibleKeys(DEFAULT_VISIBLE); showMessage("Columns reset."); }}
          />
        )}

        {/* ── Table meta bar ── */}
        <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
          <div className="flex items-center gap-3">
            <span className="text-sm text-muted-foreground">
              <strong className="text-foreground">{count.toLocaleString()}</strong> total
            </span>
            {selectedRows.length > 0 && (
              <>
                <span className="text-sm text-primary font-medium">{selectedRows.length} selected</span>
                <Button variant="ghost" size="sm" className="h-7 px-2 text-xs" onClick={() => setSelectedIds({})}>
                  Clear selection
                </Button>
              </>
            )}
          </div>
          {sort && (
            <span className="text-xs text-muted-foreground flex items-center gap-1">
              Sorted by <strong className="text-foreground mx-1">{sort.key}</strong> {sort.dir}
              <button onClick={() => setSort(null)} className="ml-1 text-primary hover:underline text-xs">✕ clear</button>
            </span>
          )}
        </div>

        {/* ── Table ── */}
        <DataTable
          columns={visibleCols}
          rows={sortedRows}
          loading={loading}
          allChecked={allChecked}
          selectedIds={selectedIds}
          onToggleAll={toggleAll}
          onToggleRow={toggleRow}
          onCellChange={handleCellChange}
          sort={sort}
          onSort={handleSort}
          colWidths={colWidths}
          onResizeStart={handleResizeStart}
          onOpenSkuPopup={openSkuPopup}
        />

        {/* ── Pagination ── */}
        <div className="flex items-center justify-between mt-4 py-3 flex-wrap gap-3">
          <span className="text-sm text-muted-foreground">
            Page <strong className="text-foreground">{page}</strong> of <strong className="text-foreground">{totalPages}</strong>
            <span className="ml-2 text-muted-foreground/60">({count.toLocaleString()} rows)</span>
          </span>
          <div className="flex items-center gap-1">
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(1)} disabled={!hasPrevPage}>«</Button>
            <Button variant="outline" size="sm" onClick={() => setPage(p => Math.max(1, p - 1))} disabled={!hasPrevPage}>‹ Prev</Button>
            {pageNumbers.map(n => (
              <Button key={n} variant={n === page ? "default" : "outline"} size="sm" className="w-8 px-0" onClick={() => setPage(n)}>{n}</Button>
            ))}
            <Button variant="outline" size="sm" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>Next ›</Button>
            <Button variant="outline" size="sm" className="px-2" onClick={() => setPage(p => p + 1)} disabled={!hasNextPage}>»</Button>
          </div>
        </div>

      </div>
    </div>
  );
}
